import { FieldViewModel, IDataLayerType, IOmniaSession, isArrayEmpty, toDate, WellKnownTextString } from "../omnia"
import { IDatalayersService } from "../types"
import { Cropping, Field, ImportTask, Layer, LayerType, ManagementTask, Parameters } from "./types"

export type TerramapApi = ReturnType<typeof createApi>

export function createApi(session: IOmniaSession, svc: IDatalayersService) {

    const { http } = session

    let shouldRefreshService = false


    return {

        load,
        refresh,

        updateParameters,
        trimLayers

    }

    async function load(refresh?: { auto: boolean }) {

        await (
            (refresh?.auto ?? true)
                ? svc.ensureInit()
                : svc.refresh()
        )

        const imports = await getImports()
        const tasks = await getManagementTasks()
        const layers = await getLayers()

        const fields = setupFields(svc.getFields(true))
        const layerTypes = setupLayerTypes(svc.getLayerTypes())

        return {
            fields,
            layerTypes,
            imports,
            tasks,
            layers
        }

    }

    async function refresh(force?: boolean) {
        await svc.refresh(force || shouldRefreshService)
        shouldRefreshService = false
    }

    function getImports() {
        const url = "/api/terramap/v1/imports"
        return http.get(url).then(r => r.data as ImportTask[])
    }

    function getManagementTasks() {
        const url = "/api/terramap/v1/mgmt"
        return http.get(url).then(r => r.data as ManagementTask[])
    }

    function getLayers() {
        const url = "/api/terramap/v1/layers?fromYear=0"
        return http.get(url).then(r => setupLayers(r.data))
    }

    async function updateParameters(parameters: Parameters, layerIds: number[]) {

        const update = {
            ...parameters,
            // This specific date is not stored as UTC,
            // so must be converted using the below conversion procedure
            date: omnia.convertDatesToJsonUtc(new Date(parameters.date)),
            layerIds
        }

        const url = "/api/terramap/v1/parameters"
        await http.post(url, update)

        shouldRefreshService = true

    }

    async function trimLayers(request: {
        fieldId: number
        croppingId: number
        boundary: WellKnownTextString
        layerIds: number[]
    }) {

        const url = "/api/terramap/v1/layers/trim"
        const response = await http.put(url, request)

        shouldRefreshService = true

        return response?.data as ManagementTask[]

    }

}


function setupLayers(layers: Layer[]): Layer[] {

    layers = (layers ?? []).map(l => ({
        ...l,
        // Dates returned by the API for layers do not have any timezome specifier
        // They are assumed to be in the locale timezone
        importDate: toDate(l.importDate, true)?.toISOString(),
        date: toDate(l.date, true)?.toISOString()
    }))

    return layers

}

function setupFields(fields: FieldViewModel[]): Field[] {

    return fields.map(f => ({
        fieldId: f.fieldId,
        name: f.fieldName,
        area: f.area,
        cropping: setupCropping(f.croppings ?? [])
    }))

}

function setupCropping(cropping: FieldViewModel[]): Cropping[] {

    return cropping.map(c => ({
        croppingId: c.croppingId,
        fieldId: c.fieldId,
        year: c.yearId,
        currentYear: c.currentYear,
        cropName: c.getCropName(),
        area: c.area,
        boundary: c.getBoundary()
    }))

}

function setupLayerTypes(types: IDataLayerType[]): LayerType[] {

    return types.map(t => ({
        typeId: t.TypeId,
        name: t.Name,
        hasDynamicScale: !isArrayEmpty(t.Ranges)
    }))

}
